import React, { useState } from "react";
import AuthTextField from "../../components/Form/AuthTextField";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import Spinner from "../../components/Spinner/Spinner";
import PaperVideoStepIndicator from "./PaperVideoStepIndicator";
import { formatFileSize } from "./formatFileSize";
import {
  lookupPaperVideo,
  requestPaperVideoLookupCode,
  verifyPaperVideoLookupCode,
  type PaperVideoLookupResult,
} from "../../api/paperVideos";

type Step = "identify" | "verify" | "result";

const STEPS = [
  { id: "identify", label: "Paper ID + email" },
  { id: "verify", label: "Verify email" },
  { id: "result", label: "Result" },
];

const primaryBtnClass =
  "eh-btn-primary block w-full py-3 text-sm font-semibold text-center disabled:opacity-60 disabled:cursor-not-allowed";

export default function PaperVideoLookupFlow() {
  const [step, setStep] = useState<Step>("identify");
  const [paperId, setPaperId] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [result, setResult] = useState<PaperVideoLookupResult | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  function clearFeedback() {
    setError("");
    setMessage("");
  }

  async function handleSubmitIdentify(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    const trimmedPaperId = paperId.trim();
    if (!trimmedPaperId) {
      setError("Please enter your paper ID.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setPaperId(trimmedPaperId);
    setIsSubmitting(true);
    try {
      const response = await requestPaperVideoLookupCode({
        paperId: trimmedPaperId,
        email: email.trim(),
      });
      if (response.ok) {
        setStep("verify");
        setMessage("We sent a 6-digit verification code to your email.");
      } else {
        setError(
          response.message ??
            "No video submission found for this paper ID and email combination.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResendCode() {
    clearFeedback();
    setIsSubmitting(true);
    try {
      const response = await requestPaperVideoLookupCode({
        paperId,
        email: email.trim(),
      });
      if (response.ok) {
        setMessage("A new verification code has been sent to your email.");
      } else {
        setError(response.message ?? "Failed to resend code.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    if (!/^\d{6}$/.test(code)) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    setIsVerifying(true);
    try {
      const verifyResponse = await verifyPaperVideoLookupCode({
        paperId,
        email: email.trim(),
        code,
      });
      if (!verifyResponse.ok) {
        setError(verifyResponse.message ?? "Code verification failed.");
        return;
      }
      const lookupResponse = await lookupPaperVideo({
        paperId,
        email: email.trim(),
      });
      if (!lookupResponse.ok) {
        setError(lookupResponse.message ?? "Failed to load video details.");
        return;
      }
      setResult(lookupResponse.data);
      setStep("result");
    } finally {
      setIsVerifying(false);
    }
  }

  return (
    <div>
      <PaperVideoStepIndicator steps={STEPS} currentStep={step} />

      {step === "identify" && (
        <form onSubmit={handleSubmitIdentify} onChange={clearFeedback}>
          <AuthTextField
            label="Paper ID"
            value={paperId}
            onChange={setPaperId}
            required
          />
          <AuthTextField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <button type="submit" className={primaryBtnClass} disabled={isSubmitting}>
            {isSubmitting ? <Spinner /> : "Send verification code"}
          </button>
        </form>
      )}

      {step === "verify" && (
        <form onSubmit={handleVerifyCode} onChange={clearFeedback}>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700 mb-4">
            <p className="font-medium text-slate-900 mb-1">Check your inbox</p>
            <p>
              Enter the 6-digit code we sent to <strong>{email}</strong>.
            </p>
          </div>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Verification code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="w-full text-center text-2xl tracking-[0.4em] border border-slate-300 bg-white px-3 py-3 rounded-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              required
            />
          </label>
          <button type="submit" className={primaryBtnClass} disabled={isVerifying}>
            {isVerifying ? <Spinner /> : "Verify and view"}
          </button>
          <div className="flex flex-col sm:flex-row gap-3 justify-center text-sm mt-4">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isSubmitting}
              className="text-slate-700 underline hover:text-slate-900 font-medium"
            >
              {isSubmitting ? "Sending…" : "Resend code"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("identify");
                clearFeedback();
              }}
              className="text-slate-700 underline hover:text-slate-900 font-medium"
            >
              Change details
            </button>
          </div>
        </form>
      )}

      {step === "result" && result && (
        <div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-sm text-green-900 mb-4">
            <p className="font-medium">
              Video found for paper <strong>{result.paperId}</strong>.
            </p>
          </div>
          <dl className="text-sm text-slate-700 space-y-1 mb-6">
            <div>
              <dt className="inline font-medium">Paper title: </dt>
              <dd className="inline">{result.paperTitle}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Conference: </dt>
              <dd className="inline">{result.conferenceLabel}</dd>
            </div>
            <div>
              <dt className="inline font-medium">File: </dt>
              <dd className="inline">
                {result.originalFileName} ({formatFileSize(result.fileSizeBytes)})
              </dd>
            </div>
            <div>
              <dt className="inline font-medium">Uploaded: </dt>
              <dd className="inline">
                {new Date(result.uploadedAt).toLocaleString()}
              </dd>
            </div>
          </dl>
          <a
            href={result.downloadUrl}
            download={result.originalFileName}
            className={`${primaryBtnClass} no-underline`}
          >
            Download video
          </a>
          <p className="text-xs text-slate-500 mt-2 text-center">
            This download link expires shortly — come back and verify again if needed.
          </p>
        </div>
      )}

      {error && <ErrorAlert className="mt-4">{error}</ErrorAlert>}
      {message && step !== "result" && (
        <p className="text-green-700 text-sm mt-4">{message}</p>
      )}
    </div>
  );
}
